import React from 'react';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import logo from '../assets/logo.png';
import { selectUser } from '../../containers/App/selectors';
import V2Icon from './V2Icon';
import V2PlayerSearch, { type V2SearchOption } from './V2PlayerSearch';
import V2LegacyReturnButton from './V2LegacyReturnButton';
import V2WorkspaceTools from './V2WorkspaceTools';
export type V2NavKey = 'players' | 'top-players' | 'subscription' | 'faq' | 'referral' | 'profile';

export type { V2SearchOption };

type V2LayoutProps = {
  children: React.ReactNode;
  activeNav: V2NavKey;
  breadcrumbs?: React.ReactNode;
  compact?: boolean;
  onToggleCompact?: () => void;
  showSearch?: boolean;
  showWorkspaceTools?: boolean;
  footerNote?: string;
  userEmail?: string;
  searchQuery?: string;
  onSearchQueryChange?: (value: string) => void;
  onSearchFocus?: () => void;
  onSearchBlur?: () => void;
  onSearchSubmit?: () => void;
  searchOptions?: V2SearchOption[];
  onSelectSearchOption?: (id: number) => void;
  searchEmptyNote?: string;
};

const navItems: {
  key: V2NavKey;
  label: string;
  to: string;
  icon: React.ComponentProps<typeof V2Icon>['name'];
}[] = [
  { key: 'players', label: 'Игроки', to: '/statistics', icon: 'players' },
  { key: 'top-players', label: 'Топ игроков', to: '/top-players', icon: 'chart' },
  { key: 'subscription', label: 'Подписка', to: '/subscription', icon: 'card' },
  { key: 'faq', label: 'FAQ', to: '/faq', icon: 'help' },
];

const V2Layout: React.FC<V2LayoutProps> = ({
  children,
  activeNav,
  breadcrumbs,
  compact = false,
  onToggleCompact: _onToggleCompact,
  showSearch = false,
  showWorkspaceTools = false,
  footerNote = 'Fish Hunter',
  userEmail,
  searchQuery = '',
  onSearchQueryChange,
  onSearchFocus,
  onSearchBlur,
  onSearchSubmit,
  searchOptions = [],
  onSelectSearchOption,
  searchEmptyNote = 'Игрок не найден.',
}) => {
  const menuDialogRef = React.useRef<HTMLDialogElement>(null);
  const currentUser = useSelector(selectUser);
  const accountInitial = (userEmail?.trim()?.[0] ?? 'U').toUpperCase();

  const handleSidebarLinkMouseDown = React.useCallback(
    (event: React.MouseEvent<HTMLAnchorElement>) => {
      if (event.button === 0) {
        event.preventDefault();
      }
    },
    [],
  );

  const blurSidebarLink = React.useCallback((event: React.MouseEvent<HTMLAnchorElement>) => {
    event.currentTarget.blur();
  }, []);

  React.useEffect(() => {
    const focused = document.activeElement;
    if (focused instanceof HTMLElement && focused.closest('.sidebar')) {
      focused.blur();
    }
  }, [activeNav]);

  React.useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (
        event.key === '/' &&
        showSearch &&
        !(event.target instanceof HTMLInputElement) &&
        !(event.target instanceof HTMLSelectElement) &&
        !(event.target instanceof HTMLTextAreaElement) &&
        !document.querySelector('dialog[open]')
      ) {
        event.preventDefault();
        (
          document.getElementById('search-player-hero') ?? document.getElementById('search-player')
        )?.focus();
      }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [showSearch]);

  return (
    <div className={`v2-theme${compact ? ' density-compact' : ''}`} data-page="statistics">
      <a className="skip-link" href="#main">
        К содержимому
      </a>

      <aside className="sidebar" aria-label="Рабочее пространство">
        <Link className="brand" to="/" aria-label="Fish Hunter — главная">
          <img src={logo} alt="Fish Hunter" />
        </Link>
        <p className="eyebrow sidebar-caption">РАБОЧЕЕ ПРОСТРАНСТВО</p>
        <nav className="side-nav" aria-label="Основная навигация">
          {navItems.map((item) => (
            <Link
              key={item.key}
              to={item.to}
              className={activeNav === item.key ? 'active' : undefined}
              aria-current={activeNav === item.key ? 'page' : undefined}
              onMouseDown={handleSidebarLinkMouseDown}
              onClick={blurSidebarLink}
            >
              <span>
                <V2Icon name={item.icon} />
              </span>
              <span>{item.label}</span>
              {activeNav === item.key ? (
                <span className="nav-arrow" aria-hidden="true">
                  ↗
                </span>
              ) : null}
            </Link>
          ))}
        </nav>
        <Link
          className={activeNav === 'referral' ? 'referral-link active' : 'referral-link'}
          to="/referral"
          aria-current={activeNav === 'referral' ? 'page' : undefined}
          onMouseDown={handleSidebarLinkMouseDown}
          onClick={blurSidebarLink}
        >
          <span>
            <V2Icon name="gift" />
          </span>
          <span>Пригласить друга</span>
        </Link>
        <div className="sidebar-bottom">
          <Link
            className="referral-link"
            to="/cooperation"
            onMouseDown={handleSidebarLinkMouseDown}
            onClick={blurSidebarLink}
          >
            <span>
              <V2Icon name="gift" />
            </span>
            <span>Сотрудничество</span>
          </Link>
          {currentUser?.isAdmin ? (
            <Link
              className="referral-link"
              to="/admin"
              onMouseDown={handleSidebarLinkMouseDown}
              onClick={blurSidebarLink}
            >
              <span>
                <V2Icon name="settings" />
              </span>
              <span>Панель админа</span>
            </Link>
          ) : null}
          <Link
            className={activeNav === 'profile' ? 'account-link active' : 'account-link'}
            onMouseDown={handleSidebarLinkMouseDown}
            onClick={blurSidebarLink}
            to="/profile"
            aria-current={activeNav === 'profile' ? 'page' : undefined}
          >
            <span className="avatar">{accountInitial}</span>
            <span>
              Мой аккаунт
              <small>{userEmail ?? 'Аккаунт'}</small>
            </span>
            <span aria-hidden="true">↗</span>
          </Link>
        </div>
      </aside>

      <div className="workspace">
        <header className="workspace-header">
          <Link className="brand mobile-brand" to="/" aria-label="Fish Hunter — главная">
            <img src={logo} alt="Fish Hunter" />
          </Link>
          {breadcrumbs}
          {showSearch ? (
            <V2PlayerSearch
              query={searchQuery}
              onQueryChange={onSearchQueryChange}
              onFocus={onSearchFocus}
              onBlur={onSearchBlur}
              onSubmit={onSearchSubmit}
              options={searchOptions}
              onSelectOption={onSelectSearchOption}
              emptyNote={searchEmptyNote}
            />
          ) : null}
          {showWorkspaceTools ? <V2WorkspaceTools /> : null}
          <button
            type="button"
            className="icon-button mobile-menu"
            aria-label="Открыть меню"
            onClick={() => menuDialogRef.current?.showModal()}
          >
            <span>
              <V2Icon name="menu" />
            </span>
          </button>
        </header>

        <main id="main">{children}</main>

        <footer className="workspace-footer">
          <span>
            <span className="status-dot"></span> FISH HUNTER{' '}
            <span className="muted">/ {footerNote}</span>
          </span>
          <V2LegacyReturnButton />
        </footer>
      </div>

      <dialog className="menu-dialog" ref={menuDialogRef} aria-labelledby="menu-title">
        <div className="dialog-heading">
          <h2 id="menu-title">Fish Hunter</h2>
          <button
            type="button"
            className="icon-button"
            aria-label="Закрыть меню"
            onClick={() => menuDialogRef.current?.close()}
          >
            ×
          </button>
        </div>
        <nav className="side-nav" aria-label="Навигация на мобильном">
          {navItems.map((item) => (
            <Link
              key={item.key}
              to={item.to}
              className={activeNav === item.key ? 'active' : undefined}
              onClick={() => menuDialogRef.current?.close()}
            >
              {item.label} {activeNav === item.key ? '↗' : ''}
            </Link>
          ))}
          <Link to="/referral" onClick={() => menuDialogRef.current?.close()}>
            Пригласить друга
          </Link>
          <Link to="/cooperation" onClick={() => menuDialogRef.current?.close()}>
            Сотрудничество
          </Link>
          <Link to="/profile" onClick={() => menuDialogRef.current?.close()}>
            Мой аккаунт
          </Link>
          {currentUser?.isAdmin ? (
            <Link to="/admin" onClick={() => menuDialogRef.current?.close()}>
              Панель админа
            </Link>
          ) : null}
          <Link to="/" onClick={() => menuDialogRef.current?.close()}>
            Главная
          </Link>
        </nav>
      </dialog>
    </div>
  );
};

export default V2Layout;
